import React, {createContext, useContext, useState, useEffect, PropsWithChildren, useCallback, useMemo} from 'react';
import {
  storeAuthToken,
  getAuthToken,
  getPendingAccount,
  updatePendingAccount,
  clearAuthData,
  upsertPendingAccount,
  mapUserToPendingAccount,
} from '../utils/auth';
import {setDatadogUser} from '../logger';
import {Organization, AccountOrganization, AccountOrganizationRole, Account} from '../models';
import {AccountServiceImpl} from '../services';

interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isVerified: boolean;
}

interface OrganizationInfo {
  organization: Organization;
  role: AccountOrganizationRole;
  accountOrganization: AccountOrganization;
}

interface AuthContextInterface {
  user: AuthUser | null;
  token: string | null;
  organizationInfo: OrganizationInfo | null;
  account: Account | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, userData?: Partial<AuthUser>) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
  updateAccount: (account: Account) => void;
  fetchAccountDetails: () => Promise<void>;
  markAsVerified: () => void;
  setOrganizationInfo: (organization: Organization, accountOrganization: AccountOrganization) => void;
  getProfileImage: () => string | null;
}

const AuthContext = createContext<AuthContextInterface>(undefined as any as AuthContextInterface);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [organizationInfo, setOrganizationInfoState] = useState<OrganizationInfo | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const accountService = useMemo(() => new AccountServiceImpl(), []);

  useEffect(() => {
    const storedToken = getAuthToken();
    const pendingAccount = getPendingAccount();

    if (storedToken && pendingAccount) {
      setToken(storedToken);
      setUser({
        id: pendingAccount.accountId,
        email: pendingAccount.email,
        firstName: pendingAccount.firstName,
        lastName: pendingAccount.lastName,
        isVerified: pendingAccount.isVerified || false,
      });
      setDatadogUser({id: pendingAccount.accountId, email: pendingAccount.email});

      if (pendingAccount.organizationInfo) {
        const orgInfo = pendingAccount.organizationInfo;
        setOrganizationInfoState({
          organization: new Organization(orgInfo.organization),
          role: orgInfo.role,
          accountOrganization: new AccountOrganization(orgInfo.accountOrganization),
        });
      }
    }

    setIsLoading(false);
  }, []);

  const login = (authToken: string, userData?: Partial<AuthUser>) => {
    storeAuthToken(authToken);
    setToken(authToken);

    if (userData) {
      const userInfo: AuthUser = {
        id: userData.id || '',
        email: userData.email || '',
        firstName: userData.firstName,
        lastName: userData.lastName,
        isVerified: userData.isVerified || false,
      };
      setUser(userInfo);
      setDatadogUser({id: userInfo.id, email: userInfo.email});

      upsertPendingAccount(userInfo);
    }
  };

  const logout = () => {
    clearAuthData();
    setToken(null);
    setUser(null);
    setDatadogUser(null);
    setOrganizationInfoState(null);
    setAccount(null);
  };

  const updateAccount = (accountData: Account) => {
    setAccount(accountData);
  };

  const getProfileImage = (): string | null => {
    return account?.profilePictureResource?.url || null;
  };

  const fetchAccountDetails = useCallback(async (): Promise<void> => {
    if (!user?.id || !token) return;

    try {
      const response = await accountService.getAccount(user.id);
      if (response.hasData() && response.data) {
        updateAccount(response.data);
      } else if (response.hasError()) {
        console.error('Failed to fetch account details:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch account details:', error);
    }
  }, [user?.id, token, accountService]);

  const updateUser = (updates: Partial<AuthUser>) => {
    if (user) {
      const updatedUser = {...user, ...updates};
      setUser(updatedUser);

      updatePendingAccount(mapUserToPendingAccount(updatedUser));
    }
  };

  const markAsVerified = () => {
    updateUser({isVerified: true});
    updatePendingAccount({
      isVerified: true,
      verifiedAt: new Date().toISOString(),
    });
  };

  const setOrganizationInfo = (organization: Organization, accountOrganization: AccountOrganization) => {
    const orgInfo: OrganizationInfo = {
      organization,
      role: accountOrganization.role,
      accountOrganization,
    };

    setOrganizationInfoState(orgInfo);

    updatePendingAccount({
      organizationInfo: {
        organization: {
          id: organization.id,
          name: organization.name,
          status: organization.status,
          industry: organization.industry,
          companyUrl: organization.companyUrl,
          companySize: organization.companySize,
          description: organization.description,
          portalUrl: organization.portalUrl,
          active: organization.active,
          createdBy: organization.createdBy,
          createdAt: organization.createdAt,
          updatedAt: organization.updatedAt,
          createdByAccount: organization.createdByAccount,
          logoImg: organization.logoImg,
          coverImage: organization.coverImage,
        },
        role: accountOrganization.role,
        accountOrganization: {
          id: accountOrganization.id,
          accountId: accountOrganization.accountId,
          active: accountOrganization.active,
          credCount: accountOrganization.credCount,
          reccoCount: accountOrganization.reccoCount,
          role: accountOrganization.role,
          createdAt: accountOrganization.createdAt,
          updatedAt: accountOrganization.updatedAt,
          organization: accountOrganization.organization,
        },
      },
    });
  };

  const value: AuthContextInterface = {
    user,
    token,
    organizationInfo,
    account,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    updateUser,
    updateAccount,
    fetchAccountDetails,
    markAsVerified,
    setOrganizationInfo,
    getProfileImage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
